import { ASSIGNABLE_ROLES, canBeAssigned, canListUsers, type Role } from "@/lib/roles"
import type { FilterOption, FilterValues } from "@/lib/table"

import type { AssignableStaffWire } from "./api"
import { QUEUE_FILTER_IDS } from "./queue-filters"

/*
   What the Assignee filter offers, by who is asking.

   | Role             | Options                                  |
   |------------------|------------------------------------------|
   | admin            | "Me", then every other assignable person |
   | reviewer / sme   | "Me" only (they cannot read /users)      |
   | viewer / client  | no Assignee filter at all                |

   "Me" is the legacy queue's "Mine": it is stored as `me`, not as an id, so
   `?assignee=me` means "assigned to whoever opens the link" — a colleague
   opening it sees their own queue, as with legacy's `?mine=1`. It becomes the
   viewer's id only when the request is built.

   A viewer or client can never be assigned a review, so a "Me" for them
   would always return nothing — the filter is left out, as legacy hides Mine
   from clients. There is no "—" option: the server filters by who holds a
   review, not who does not.
*/

export interface AssigneeViewer {
  id: string
  role: Role
}

export const ME = "me"
export const ME_LABEL = "Me"

/** `null` means the filter does not apply to this viewer and is left out of the panel. */
export function assigneeFilterOptions(
  viewer: AssigneeViewer | null,
  roster: readonly AssignableStaffWire[] | undefined,
): FilterOption[] | null {
  if (!viewer || !canBeAssigned(viewer.role)) return null

  const me: FilterOption = { value: ME, label: ME_LABEL }
  if (!canListUsers(viewer.role) || !roster) return [me]

  const others = roster
    .filter((person) => person.is_active && isAssignable(person.role))
    // "Me" already covers the viewer.
    .filter((person) => String(person.id) !== viewer.id)
    .map((person) => ({ value: String(person.id), label: person.full_name ?? person.email }))
    .sort((a, b) => a.label.localeCompare(b.label))
  return [me, ...others]
}

/**
 * Who a request may name as assignee, for this viewer. Absent when the
 * filter does not apply to them, so nothing is sent.
 *
 * An admin may name anyone. Everyone else may only name themselves, so an id
 * in a link someone else wrote is never sent on their behalf.
 */
export interface AssigneeScope {
  viewerId: string
  anyone: boolean
}

export function assigneeScopeFor(viewer: AssigneeViewer | null): AssigneeScope | undefined {
  if (!viewer || !canBeAssigned(viewer.role)) return undefined
  return { viewerId: viewer.id, anyone: canListUsers(viewer.role) }
}

function isAssignable(role: string): boolean {
  return (ASSIGNABLE_ROLES as readonly string[]).includes(role)
}

/**
 * The viewer's own id in a link's `?assignee=` read as "Me".
 *
 * The viewer is never one of their own options ("Me" stands for them), so a link a
 * colleague made by picking them — `?assignee=1`, opened by user 1 — was dropped as an
 * unknown option: no filter, no chip, the whole queue. Read as "Me", it filters to their
 * reviews, which is what the link asked for. Everyone else opening it still sees user 1 by
 * name. The URL is not rewritten. Left alone when the filter does not apply to the viewer.
 */
export function viewerIdAsMe(values: FilterValues, scope: AssigneeScope | undefined): FilterValues {
  const id = QUEUE_FILTER_IDS.assignee
  const assignees = values[id]
  if (!scope || !Array.isArray(assignees) || !assignees.includes(scope.viewerId)) return values
  const mapped = assignees.map((value) => (value === scope.viewerId ? ME : value))
  // De-duplicated: a link holding both "me" and the viewer's id is one "Me".
  return { ...values, [id]: [...new Set(mapped)] }
}
